import React from 'react';

/** Tiroir « Statistiques et profil de build » — dérivé des vraies métriques joueur. */

interface VehicleMetrics {
  total: number;
  pret: number;
  mission: number;
  maintenance: number;
  panne: number;
  avgKm: number;
  readinessPercent: number;
}
interface PersonnelMetrics {
  total: number;
  actif: number;
  inactif: number;
  avgAge: number;
  nearRetirement: number;
  activeRate: number;
}
interface TypeBreakdown {
  typeName: string;
  count: number;
  vehicles: number;
  personnel: number;
}
interface Props {
  vehicles: VehicleMetrics;
  personnel: PersonnelMetrics;
  breakdown: TypeBreakdown[];
  alerts: string[];
  sitesCount: number;
}

const Tile: React.FC<{ label: string; value: React.ReactNode; tone?: 'green' | 'amber' | 'red' | 'blue' }> = ({
  label,
  value,
  tone,
}) => {
  const color = tone === 'green' ? '#6ee7b7' : tone === 'amber' ? '#fcd34d' : tone === 'red' ? '#fca5a5' : tone === 'blue' ? '#7dd3fc' : '#dce5f2';
  return (
    <div style={{ background: '#0e1725', border: '1px solid #223046', borderRadius: 6, padding: '10px 12px' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: 'monospace' }}>{value}</div>
      <div style={{ fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: '#8096ae', marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
};

const grid = (min = 120): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`,
  gap: 8,
});

const StatsDrawer: React.FC<Props> = ({ vehicles, personnel, breakdown, alerts, sitesCount }) => (
  <>
    <p className="dintro">
      Photographie de votre dispositif en temps réel (données réelles du compte). Utile pour arbitrer
      recrutement, achat d'engins et implantation.
    </p>

    <div className="block">
      <div className="block__h">Parc d'engins</div>
      <div className="block__b">
        <div style={grid()}>
          <Tile label="Total" value={vehicles.total} />
          <Tile label="Disponibles" value={vehicles.pret} tone="green" />
          <Tile label="En mission" value={vehicles.mission} tone="amber" />
          <Tile label="Indispo (maint./panne)" value={vehicles.maintenance + vehicles.panne} tone="red" />
          <Tile label="Disponibilité" value={`${vehicles.readinessPercent} %`} tone="blue" />
          <Tile label="Km moyen" value={vehicles.avgKm.toLocaleString('fr-FR')} />
        </div>
      </div>
    </div>

    <div className="block" style={{ marginTop: 10 }}>
      <div className="block__h">Personnel</div>
      <div className="block__b">
        <div style={grid()}>
          <Tile label="Effectif" value={personnel.total} />
          <Tile label="Actifs" value={personnel.actif} tone="green" />
          <Tile label="Inactifs" value={personnel.inactif} tone="amber" />
          <Tile label="Taux d'activité" value={`${personnel.activeRate} %`} tone="blue" />
          <Tile label="Âge moyen" value={personnel.avgAge} />
          <Tile label="Proches retraite" value={personnel.nearRetirement} tone={personnel.nearRetirement ? 'red' : undefined} />
        </div>
      </div>
    </div>

    <div className="block" style={{ marginTop: 10 }}>
      <div className="block__h">Implantation — {sitesCount} centre(s)</div>
      <div className="block__b">
        {breakdown.length === 0 ? (
          <div className="empty">Aucun centre.</div>
        ) : (
          <div style={grid(180)}>
            {breakdown.map((b) => (
              <div key={b.typeName} style={{ background: '#0e1725', border: '1px solid #223046', borderRadius: 6, padding: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: '#dce5f2' }}>{b.typeName}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 12, fontSize: 10, color: '#9fb0c6', fontFamily: 'monospace' }}>
                  <span>🏢 {b.count}</span>
                  <span>🚒 {b.vehicles}</span>
                  <span>👤 {b.personnel}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {alerts.length ? (
      <div className="block block--alert" style={{ marginTop: 10 }}>
        <div className="block__h">Alertes ressources</div>
        <div className="block__b">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: '#fde68a', lineHeight: 1.6 }}>
            {alerts.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      </div>
    ) : null}
  </>
);

export default StatsDrawer;
